import { defineComponent, ref, watch, nextTick, computed } from 'vue';
import { parsePhoneNumber, getExample, getRegionCodeForCountryCode } from 'awesome-phonenumber';
import { allCountries } from './helpers';
import { useRules } from '@/utils/rules';
export default defineComponent({
  props: {
    value: {
      type: String,
      default: ''
    },
    defaultCountry: {
      type: [Number, String],
      default: 'us'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: String,
      default: ''
    },
    onlyPhone: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    canDoubleClickToEdit: {
      type: Boolean,
      default: false
    },
    readModeEmptyState: {
      type: String,
      default: null
    },
    hideDetails: {
      type: [Boolean, String],
      default: false
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  setup(props, {
    emit
  }) {
    var _getExample$number;
    const localValue = ref(props.value);
    const inputRef = ref(null);
    const countryList = allCountries.map(country => ({
      name: country[0],
      iso2: country[1].toUpperCase(),
      dialCode: country[2],
      priority: country[3] || 0,
      areaCodes: country[4] || null
    }));
    const localLabel = ref(props.label);
    if (!props.label) {
      localLabel.value = `${props.onlyPhone ? 'Phone Number' : 'Email or Phone'}`;
    }
    const isPhoneNumber = ref(props.onlyPhone);
    const inputChanged = phone => {
      if (!props.onlyPhone) {
        const value = phone.search('[A-Za-z]') === -1 && phone !== '';
        isPhoneNumber.value = value;
      }
      // as we are typing a phone number check if we can get a country code from what was typed
      // if so automatically update the selected country to match that code
      if (isPhoneNumber.value && phone !== null && phone !== void 0 && phone.length) {
        // if our phone number starts with + then check for country code
        // if not +, assume country code was picked in selector
        if (phone[0] === '+') {
          const result = getRegionCodeForCountryCode(parseInt(phone));
          const found = countryList.find(country => country.iso2.toLowerCase() === result.toLowerCase());
          if (found) {
            selectedCountry.value = found;
          }
        }
      }
      emit('input', localValue.value);
      emit('change', localValue.value);
      nextTick(() => {
        var _inputRef$value, _inputRef$value$input;
        (_inputRef$value = inputRef.value) === null || _inputRef$value === void 0 ? void 0 : (_inputRef$value$input = _inputRef$value.inputRef) === null || _inputRef$value$input === void 0 ? void 0 : _inputRef$value$input.focus();
      });
    };
    const selectedCountry = ref(countryList.find(country => country.iso2.toLowerCase() === props.defaultCountry.toLowerCase()));
    const placeholder = ref(((_getExample$number = getExample(selectedCountry.value.iso2, 'mobile').number) === null || _getExample$number === void 0 ? void 0 : _getExample$number.e164) || '');
    const {
      emailRules
    } = useRules();
    const phoneRules = [phone => {
      // validate if the typed in phone number is valid
      const result = parsePhoneNumber(phone || '', {
        regionCode: selectedCountry.value.iso2
      });
      return phone && !result.valid ? `${props.label} is not valid` : props.required && !phone ? `${props.label} is required` : true;
    }];
    const updatePlaceholder = () => {
      var _getExample$number2;
      placeholder.value = ((_getExample$number2 = getExample(selectedCountry.value.iso2, 'mobile').number) === null || _getExample$number2 === void 0 ? void 0 : _getExample$number2.e164) || '';
    };
    const formatNumber = () => {
      const result = parsePhoneNumber(localValue.value || '', {
        regionCode: selectedCountry.value.iso2
      });
      if (result.valid) {
        localValue.value = result.number.international;
      }
      emit('input', localValue.value);
      emit('change', localValue.value);
    };
    watch(() => props.value, () => {
      localValue.value = props.value;
      if (props.value !== '') {
        inputChanged(localValue.value);
      }
    }, {
      immediate: true
    });
    watch(() => props.label, () => {
      localLabel.value = props.label;
    });
    const valid = computed(() => {
      var _inputRef$value2;
      return !props.errorMessages && ((_inputRef$value2 = inputRef.value) === null || _inputRef$value2 === void 0 ? void 0 : _inputRef$value2.validate(true));
    });
    const validate = () => {
      var _inputRef$value3;
      return (_inputRef$value3 = inputRef.value) === null || _inputRef$value3 === void 0 ? void 0 : _inputRef$value3.validate();
    };
    return {
      countryList,
      selectedCountry,
      formatNumber,
      localValue,
      isPhoneNumber,
      inputChanged,
      placeholder,
      updatePlaceholder,
      inputRef,
      phoneRules,
      emailRules,
      localLabel,
      valid,
      validate
    };
  }
});